/**
 * Creates the map.
 * The map is an OpenLayers map which connects to the "map" div.
 */
import { Map, View } from "ol";
import TileLayer from "ol/layer/Tile";
import OSM from "ol/source/OSM";
import WMS from "ol/source/TileWMS";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import GeoJSON from "ol/format/GeoJSON";
import { Style, Fill } from "ol/style";
import { createPinLayer } from "./pinCreator";
import GeoJSONTerminator from "./terminator";
import "ol/ol.css";
import Overlay from "ol/Overlay";
import { transform } from "ol/proj";
import { createPinModal } from "./modalCreator";
import pinImages from "../data/pinImages";
import locationData from '../data/SNEWS World Map - Points.json';

const INITIAL_ZOOM = 2;
const MAX_ZOOM = 20;
const INITIAL_LON_LAT = [0, -5];
const MAP_EXTENTS = [-180, -95, 180, 90];
const TERMINATOR_OPACITY = 0.3;
const MAP_PROJECTION = "EPSG:4326";

import homeIMG from '/images/home.png';
import menuIMG from '/images/menu.png';
import questionIMG from '/images/questionmark.png';
import exitIMG from '/images/exit.png';

/**
 * Create the background layers.
 */
const OSMLayer = new TileLayer({
  source: new OSM({
    wrapX: false,
  }),
  zIndex: -10,
});

const WMSLayer = new TileLayer({
  source: new WMS({
    url: "https://ahocevar.com/geoserver/wms",
    params: {
      LAYERS: "ne:NE1_HR_LC_SR_W_DR",
    },
    wrapX: false,
  }),
  zIndex: -10,
});

/**
 * Create the day/night terminator.
 */
const geoJSONTerminator = new GeoJSONTerminator();
const terminatorLayer = new VectorLayer({
  source: new VectorSource({
    features: new GeoJSON().readFeatures(geoJSONTerminator, {
      featureProjection: MAP_PROJECTION,
    }),
  }),
  style: new Style({
    fill: new Fill({
      color: "rgb(0, 0, 0)",
    }),
  }),
  opacity: TERMINATOR_OPACITY,
});

/**
 * Set up the view for map boundaries, etc.
 */
const view = new View({
  projection: MAP_PROJECTION,
  center: INITIAL_LON_LAT,
  zoom: INITIAL_ZOOM,
  maxZoom: MAX_ZOOM,
  extent: MAP_EXTENTS,
  smoothExtentConstraint: false,
});

/**
 * Set up the pin popover overlay for location information.
 */
let currentPopover = null;
const popoverOverlay = new Overlay({
  element: document.createElement("div"),
});

/**
 * Create the actual map with the layers, overlays, and view defined above.
 */
const map = new Map({
  target: "map",
  layers: [OSMLayer, terminatorLayer],
  overlays: [popoverOverlay],
  view: view,
});

/**
 * Set up click events for showing pin information.
 */
map.on("singleclick", (event) => {
  currentPopover?.hide();
  let foundPin = false;

  map.forEachFeatureAtPixel(event.pixel, (feature, layer) => {
    if (foundPin || layer.get("purpose") !== "pin") return;
    foundPin = true;

    let lonLat = feature.getGeometry().getCoordinates();

    if (MAP_PROJECTION != "EPSG4326") {
      lonLat = transform(lonLat, MAP_PROJECTION, "EPSG:4326");
    }

    updatePinPopover(layer.get("otherInfo"), layer.get("name"), lonLat[0], lonLat[1]);
  });
});

// Create the question image element
const questionImage = document.createElement('img');
questionImage.src = questionIMG; 
questionImage.alt = 'Help';
questionImage.classList.add('question-image');

// Append the question image to the map container
const mapContainer = document.getElementById('map');
mapContainer.appendChild(questionImage);

questionImage.addEventListener('click', handleHelpClick);

const help = document.getElementById('help');

function handleHelpClick() {
  if (help) {
    help.classList.toggle('help-visible');
    return; // Exit the function
  } 
}

// Create the close image element
const exitImage = document.createElement('img');
exitImage.src = exitIMG; 
exitImage.alt = 'Close';
exitImage.classList.add('exit-image');

help.appendChild(exitImage);

exitImage.addEventListener('click', handleHelpClick);


// Create the menu image element
const menuImage = document.createElement('img');
menuImage.src = menuIMG; // Adjust the path as needed
menuImage.alt = 'Menu';
menuImage.classList.add('menu-image'); // Add a class for styling if necessary

// Append the menu image to the map container
mapContainer.appendChild(menuImage);

// Add event listener to handle menu click
menuImage.addEventListener('click', handleMenuClick);

function handleMenuClick() {
  const sidebar = document.getElementById('sidebar');

  // If sidebar exists, toggle its visibility
  if (sidebar) {
    sidebar.classList.toggle('sidebar-visible');
    return; // Exit the function
  }

  // Create the sidebar element if it doesn't exist
  const sidebarElement = document.createElement('div');
  sidebarElement.id = 'sidebar';
  sidebarElement.classList.add('sidebar', 'sidebar-visible'); // Initially visible

  // Create the facility filter list
  const filterList = document.createElement('ul');
  filterList.classList.add('legend-list');

  // Create and append the list items for each facility category
  const filterItems = [
    { className: 'legend-red', textContent: 'Professional Observatories', value: 'professional observatory' },
    { className: 'legend-purple', textContent: 'Private Observatories', value: 'private observatory' },
    { className: 'legend-yellow', textContent: 'Current Neutrino Facilities', value: 'current' },
    { className: 'legend-green', textContent: 'Future Neutrino Facilities', value: 'future' },
    { className: 'legend-blue', textContent: 'Soon-Offline Neutrino Facilities', value: 'soon offline' }
  ];

  filterItems.forEach(item => {
    const listItem = document.createElement('li');
    listItem.classList.add(item.className);
    listItem.textContent = item.textContent;
    listItem.setAttribute('data-value', item.value); // Set the value attribute for data retrieval
    listItem.addEventListener('click', function() {
      onFilterChange(this.getAttribute('data-value')); // Call onFilterChange with the selected value
    });
    filterList.appendChild(listItem);
  });

  // Append the facility filter list to the sidebar
  sidebarElement.appendChild(filterList);

  // Append the sidebar to the map container
  const mapContainer = document.getElementById('map');
  mapContainer.appendChild(sidebarElement);
}

// Create the home image element
const homeImage = document.createElement('img');
homeImage.src = homeIMG; // Adjust the path as needed
homeImage.alt = 'Home';
homeImage.classList.add('home-image'); // Add a class for styling if necessary

// Append the home image to the map container
mapContainer.appendChild(homeImage);

// Add event listener to handle home click
homeImage.addEventListener('click', handleHomeClick);

// Function to handle home click event
function handleHomeClick() {
  window.location.href = 'https://snews2.org/';
}

/**
 * Asynchronously requests weather information and displays information about pin.
 */
const tabContainer = document.getElementById('tab-container');

const pinName = document.getElementById('pin-name');
const weatherContent = document.getElementById('weather');
const infoContent = document.getElementById('info');

const tabDiv = document.querySelector(".tab");

const updatePinPopover = (info, name, lon, lat) => {
  let lonRounded = Math.round(lon * 100) / 100;
  let latRounded = Math.round(lat * 100) / 100;
  const tabContent = createPinModal(name, info, lonRounded, latRounded);

  // Update tab container content
  pinName.innerHTML = tabContent.name;
  weatherContent.innerHTML = tabContent.weather;
  infoContent.innerHTML = tabContent.info;

  // Show tab container with animation
  tabContainer.style.bottom = '20px'; // Adjust position as needed
};

// Add event listener to hide tab container when clicking outside of it
document.addEventListener('click', function(event) {
  const isClickInsideTabContainer = tabContainer.contains(event.target);
  if (!isClickInsideTabContainer) {
    tabContainer.style.bottom = '-100%'; // Hide weather container with animation
  }
});

// Add event listener to change the tab contents.
tabDiv.addEventListener('click', event => {
  openTab(event, event.target.id.slice(0, -4))
})

/**
 * Controls tabbing for bottom modal.
*/

const openTab = (evt, tab) => {
  // Get all elements with class="tabcontent" and hide them
  const tabcontent = document.getElementsByClassName("tabcontent");
  for (let i = 0; i < tabcontent.length; i++) {
    tabcontent[i].style.display = "none";
  }

  // Get all elements with class="tablinks" and remove the class "active"
  const tablinks = document.getElementsByClassName("tablinks");
  for (let i = 0; i < tablinks.length; i++) {
    tablinks[i].className = tablinks[i].className.replace("active", "");
  }

  // Show the current tab, and add an "active" class to the button that opened the tab
  document.getElementById(tab).style.display = "block";
  evt.currentTarget.className += " active";
} 


/**
 * Toggles the day/night terminator.
 */
let showingTerminator = true;
const toggleTerminator = () => {
  if (showingTerminator) {
    map.removeLayer(terminatorLayer);
  } else {
    map.addLayer(terminatorLayer);
  }
  showingTerminator = !showingTerminator;
};


/**
 * Filters for certain pins based on their type.
 */
const filterPins = (filters) => {
  map.getLayers().forEach((layer) => {
    if (layer.get("purpose") !== "pin") return;

    const type = layer.get("type");
    const isVisible = filters.includes(type) || filters.includes("all");
    layer.setVisible(isVisible);
  });
};

/**
 * Handler for toggling map layers.
 */
let showingOSM = true;
const toggleMapLayers = () => {
  if (showingOSM) {
    map.removeLayer(OSMLayer);
    map.addLayer(WMSLayer);
  } else {
    map.removeLayer(WMSLayer);
    map.addLayer(OSMLayer);
  }

  showingOSM = !showingOSM;
};

/**
 * Add pins here.
 */

const addPins = () => {
  // Add facilities from internal JSON file

  Object.keys(locationData).forEach((item) => {
    let facility = locationData[item];
    
    // default setting for export JSON add-on if cell is empty is null value
    if (facility.aliases == null){
      facility.aliases = "";
    }
    
    // append aliases to name if present
    if (facility.aliases !== ""){
      let aliases = facility.aliases.split(",")
      for (let i = 0; i < aliases.length; ++i) {
        item += " / " + aliases[i];
       }
    }
    
    // Save other info for display on modal
    let otherInfo = {};
    Object.keys(facility).forEach((field) => {
      if (!["lat", "lon", "status", "aliases"].includes(field)){
        otherInfo[field] = facility[field];
      }
    })

    let image;
    switch (facility.status) {
      case "current":
        image = pinImages.yellow;
        break;
      case "soon offline":
        image = pinImages.blue;
        break;
      case "future":
        image = pinImages.green;
        break;
      case "professional observatory":
        image = pinImages.red;
        break;
      case "private observatory":
        image = pinImages.purple;
        break;
    }

    map.addLayer(
      createPinLayer(
        facility.lon,
        facility.lat,
        item,
        otherInfo,
        facility.status,
        image
      )
    );
  });
};

// Function to filter pins based on facility name and aliases
const filterPinsByName = (searchQuery) => {
  // Convert searchQuery to lowercase for case-insensitive comparison
  searchQuery = searchQuery.toLowerCase().trim();

  map.getLayers().forEach((layer) => {
    if (layer.get("purpose") !== "pin") return;

    const name = layer.get("name").toLowerCase(); // Facility name
    const aliases = layer.get("aliases") || []; // Facility aliases

    // Check if the facility name or any alias matches the search query
    const isVisible = name.includes(searchQuery) || aliases.some(alias => alias.toLowerCase().includes(searchQuery));
    layer.setVisible(isVisible);
  });
};

addPins();

export { filterPins, toggleMapLayers, toggleTerminator, filterPinsByName, openTab };
